import React, { useMemo } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { SnackbarProvider } from 'notistack';

import { queryClient } from '@/lib/queryClient';
import { buildTheme } from '@/theme';
import { useUiStore } from '@/store/uiStore';
import { ConfirmProvider } from '@/components/common/ConfirmProvider';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import App from './App';
import './index.css';

// Root wires the theme (driven by the ui store) and the global providers.
function Root() {
  const mode = useUiStore((s) => s.colorMode);
  const theme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={3800}
      >
        <QueryClientProvider client={queryClient}>
          <ConfirmProvider>
            <ErrorBoundary>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </ErrorBoundary>
          </ConfirmProvider>
          {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />}
        </QueryClientProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>,
);
